import {getTestProps} from '@benchling/js-test-utils';
import {groupBy} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec, LookupStepType, SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {StaticLookupConfigSummaryChip} from 'lookup-config/LookupConfigSummaryChip';
import {SectionWrapper} from 'metadata/MetadataEditorInfoSection';
import {
  FlowchartValidationErrorType,
  ProcessFlowchartErrorType,
  ProcessFlowchartValidationError,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {runLookupConfigModalForProcessSchemaMapper} from 'processes/modals/field-mapping/SchemaFieldLookup';
import {getConditionalMappingCheckboxProps} from 'processes/settings/graph-builder-flowchart-config/conditionalMappingUtils';
import {buildDefaultSchemaFieldMapperConfigFromDataFlowEdge} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/buildSchemaMapperConfigFromStaticData';
import {getFirstFieldIdFromLookupConfig} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/buildStaticDataFromFlowchartConfigGraphQL';
import {
  doesDataFlowEdgeHandleReferenceRow,
  getFieldInfoFromDataFlowEdgeHandle,
  getSchemaFieldIdFromRowIdentifier,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {getClientId} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/getClientId';
import isLookupConfigCustomMapping from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/isLookupConfigCustomMapping';
import {
  CustomMappingData,
  FlowchartEdge,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  TaskSchemaNodeSectionType,
  TransitionSource,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  TaskSchemaNode,
  TaskSchemaNodeRowData,
  isInteractiveTaskNode,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {
  getCustomMappingForEdge,
  getTransitionSourceForEdgeWithDefault,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/transitionLookupUtils';
import {useTransitionsContext} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionsContext';
import {getExecutionNodeTypeIdentifierForNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/useFlowchartStateAnalytics';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {BLUE_LIGHT, GRAY_DARK, RED, RED_POWDER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';
import {ValidationErrorRenderer} from './ValidationErrorRenderer';

export interface TransitionFieldInfoSectionProps {
  selectedFieldId: string;
  node: TaskSchemaNode;
  flowchart: {
    nodes: Array<FlowchartNode>;
    edges: Array<FlowchartEdge>;
  };
  flowchartTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  allSchemasById: SchemaById;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
  onChangeCustomMappingData: (edgeId: string, newCustomMappingData: CustomMappingData) => void;
  onChangeSelectedFieldId: (fieldId: string | null) => void;
  transitionSourceByNodeIdPair: Record<string, TransitionSource>;
  locked?: boolean;
  validationErrors: Array<ProcessFlowchartValidationError>;
}

function FieldDisplay(props: {
  field: {fieldIcon: BenchlingIconName; fieldName: string | JSX.Element};
}): JSX.Element {
  return (
    <div className={css(styles.fieldDisplay)}>
      <BenchlingIcon icon={props.field.fieldIcon} />
      {props.field.fieldName}
    </div>
  );
}

type MappingsTableProps = TransitionFieldInfoSectionProps & {
  edges: Array<FlowchartEdge>;
  tableDescription: string;
};

type IntermediateMappingsTableProps = TransitionFieldInfoSectionProps & {
  tableDescription: string;
};

function getMappingSourceSchema(
  edge: FlowchartEdge,
  nodes: Array<FlowchartNode>,
  allSchemasById: SchemaById,
  transitionSource: TransitionSource
): Schema | null {
  const sourceNode = nodes.find((node) => node.id === edge.source);
  assert(sourceNode != null, 'Expected source node to be non-null', {edge});
  assert(isInteractiveTaskNode(sourceNode), 'Expected source node to be a task node', {edge});

  const fromSchemaId = sourceNode.data.taskSchemaId;
  if (fromSchemaId == null) {
    return null;
  }
  const fromTaskSchema = allSchemasById[fromSchemaId]! as ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  const fromSchema =
    transitionSource === TransitionSource.OUTPUTS ? fromTaskSchema.outputSchema : fromTaskSchema;
  if (fromSchema == null) {
    return null;
  }
  return fromSchema;
}

function EditMappingButton(props: {
  fromTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  transitionSourceSchema: Schema;
  lookupConfig: LookupConfigSpec;
  flowchartTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  onChangeLookupConfig: (lookupConfig: LookupConfigSpec | null) => void;
  allSchemasById: SchemaById;
  toSchemaField: SchemaField;
  fromSchemaFieldId: string;
  fromSchemaFieldSectionName: string;
  isCustomLookupConfig: boolean;
  locked?: boolean;
}): JSX.Element {
  const context = NodeDetailsSidepanelContext.useContext();

  const getAdditionalLookupConfigValidationMessage = (newLookupConfig: LookupConfigSpec): string | null => {
    let fromFieldSchema: Schema;
    switch (props.fromSchemaFieldSectionName) {
      case TaskSchemaNodeSectionType.OUTPUTS:
        if (props.fromTaskSchema.outputSchema == null) {
          return 'This node uses outputs to populate tasks but the previous node does not have an output schema.';
        }

        fromFieldSchema = props.fromTaskSchema.outputSchema;
        break;
      case TaskSchemaNodeSectionType.MAP_FROM_START:
        fromFieldSchema = props.flowchartTaskSchema;
        break;
      case TaskSchemaNodeSectionType.INPUTS:
        fromFieldSchema = props.fromTaskSchema;
        break;
      default:
        assert(false, `Expected a valid from schema field section name ${props.fromSchemaFieldSectionName}`);
    }
    const schemaFieldId = getFirstFieldIdFromLookupConfig(newLookupConfig);
    const fromSchemaField = fromFieldSchema.allFields.find(
      (field: SchemaField) => field.apiId === props.fromSchemaFieldId
    )!;
    const errorMessage = `The lookup must start with the ${fromSchemaField.name} field in the '${props.fromSchemaFieldSectionName}' section.`;
    if (schemaFieldId == null) {
      return errorMessage;
    } else if (schemaFieldId !== props.fromSchemaFieldId) {
      return errorMessage;
    }
    return null;
  };

  return (
    <Tooltip text={props.isCustomLookupConfig ? 'Edit lookup' : 'Configure lookup'}>
      <Button
        buttonStyle={ButtonStyle.LINK}
        onClick={() => {
          runLookupConfigModalForProcessSchemaMapper({
            fromSchema: props.transitionSourceSchema,
            lookupConfig: props.lookupConfig,
            flowchartTaskSchema: props.flowchartTaskSchema,
            onSubmit: (lookupConfig) => {
              const newLookupConfig = isLookupConfigCustomMapping(lookupConfig, props.fromSchemaFieldId)
                ? lookupConfig
                : null;
              if (newLookupConfig != null) {
                analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.COMPLEX_LOOKUP_MADE, {
                  flowchartType: context.flowchartEditorConfigurator.flowchartTypeName,
                  actionMethod: ActionMethod.BUTTON_CLICK,
                  actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
                  nodeType: getExecutionNodeTypeIdentifierForNode(context.selectedNode),
                });
              }
              props.onChangeLookupConfig(newLookupConfig);
            },
            allSchemasById: props.allSchemasById,
            toSchemaField: props.toSchemaField,
            getAdditionalLookupConfigValidationMessage,
            canLookupSourceObjectBeModified: false,
          });
        }}
        disabled={props.locked}
        icon={<BenchlingIcon icon="square-list-magnifying-glass" className={css(styles.actionIcon)} />}
        dataTestAttrs={{
          component: 'TransitionMappingsTable',
          element: props.isCustomLookupConfig ? 'editMappingButton' : 'configureMappingButton',
        }}
      />
    </Tooltip>
  );
}

function EditMappingLookupCell(props: {
  targetField: TaskSchemaNodeRowData;
  sourceField: TaskSchemaNodeRowData;
  dataFlowEdge: FlowchartStaticEdgeData;
  nodes: Array<FlowchartNode>;
  allSchemasById: SchemaById;
  flowchartTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  transitionSource: TransitionSource;
  isCustomLookupConfig: boolean;
  lookupConfig: LookupConfigSpec;
  onChangeLookupConfig: (lookupConfig: LookupConfigSpec | null) => void;
  locked?: boolean;
}): JSX.Element | null {
  assert(props.dataFlowEdge.type === ProcessFlowchartEdgeType.DATA_FLOW, 'Expected a data flow edge');
  const targetFieldId = getSchemaFieldIdFromRowIdentifier(props.targetField.clientId);

  const sourceNode = props.nodes.find((node) => node.id === props.dataFlowEdge.source);
  assert(sourceNode != null, 'Expected source node to be non-null', {edge: props.dataFlowEdge});
  assert(isInteractiveTaskNode(sourceNode), 'Expected source node to be a task node', {
    edge: props.dataFlowEdge,
  });
  const fromSchemaId = sourceNode.data.taskSchemaId;
  if (fromSchemaId == null) {
    return null;
  }

  const fromSchemaFieldId = getSchemaFieldIdFromRowIdentifier(props.sourceField.clientId);
  const {sectionType} = getFieldInfoFromDataFlowEdgeHandle(props.dataFlowEdge.sourceHandle);

  const fromTaskSchema = props.allSchemasById[
    fromSchemaId
  ]! as ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  const fromSchema =
    props.transitionSource === TransitionSource.OUTPUTS ? fromTaskSchema.outputSchema : fromTaskSchema;

  if (fromSchema == null) {
    return null;
  }

  const targetNode = props.nodes.find((node) => node.id === props.dataFlowEdge.target);
  assert(targetNode != null, 'Expected target node to be non-null', {edge: props.dataFlowEdge});
  assert(isInteractiveTaskNode(targetNode), 'Expected target node to be a task node', {
    edge: props.dataFlowEdge,
  });
  const toSchemaId = targetNode.data.taskSchemaId;
  if (toSchemaId == null) {
    return null;
  }
  const toTaskSchema = props.allSchemasById[toSchemaId]! as ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  const toSchema =
    toTaskSchema.executionType === ProcessExecutionType.FLOWCHART ? toTaskSchema.outputSchema : toTaskSchema;
  assert(toSchema != null, 'toSchema should not be null');

  const toSchemaField = toSchema.allFields.find((field: SchemaField) => field.apiId === targetFieldId);
  assert(toSchemaField != null, 'toSchemaField should not be null');

  const lookupConfig = {...props.lookupConfig};
  // We do not support custom lookup configs for the PROCESS_TASK_DEFINITION_FIELD so when the user configures
  // one we translate to a SCHEMA_FIELD lookup step under the hood
  if (!props.isCustomLookupConfig) {
    lookupConfig.lookupSteps = [...lookupConfig.lookupSteps];
    lookupConfig.lookupSteps.pop();
    lookupConfig.lookupSteps.push({
      type: LookupStepType.SCHEMA_FIELD,
      schemaField: fromSchemaFieldId,
    });
  }
  return (
    <Cell className={css(styles.actionsCell)}>
      <div className={css(styles.editMappingButtonContainer)}>
        <EditMappingButton
          fromTaskSchema={fromTaskSchema}
          transitionSourceSchema={fromSchema}
          lookupConfig={lookupConfig}
          flowchartTaskSchema={props.flowchartTaskSchema}
          onChangeLookupConfig={props.onChangeLookupConfig}
          allSchemasById={props.allSchemasById}
          toSchemaField={toSchemaField}
          fromSchemaFieldId={fromSchemaFieldId}
          fromSchemaFieldSectionName={sectionType}
          isCustomLookupConfig={props.isCustomLookupConfig}
          locked={props.locked}
        />
        {props.isCustomLookupConfig && (
          <>
            <span>|</span>
            <Tooltip text="Reset lookup">
              <Button
                buttonStyle={ButtonStyle.LINK}
                onClick={() => {
                  props.onChangeLookupConfig(null);
                }}
                icon={<BenchlingIcon icon="trash-can" className={css(styles.actionIcon)} />}
                dataTestAttrs={{
                  component: 'TransitionMappingsTable',
                  element: 'resetMappingButton',
                }}
                disabled={props.locked}
              />
            </Tooltip>
          </>
        )}
      </div>
    </Cell>
  );
}

function NoMappings(): JSX.Element {
  return (
    <div className={css(styles.noMappings)}>
      <span className={css(styles.noMappingsText)}>
        Draw mappings to this field to view & edit them here.
      </span>
    </div>
  );
}

export function TransitionMappingsTable(props: MappingsTableProps): JSX.Element {
  const {didInitialConfigHaveConditionalMapping} = NodeDetailsSidepanelContext.useContext();
  const getTargetField = (rowIndex: number): TaskSchemaNodeRowData | null => {
    const edge = props.edges[rowIndex];
    const targetNode = props.flowchart.nodes.find((node) => node.id === edge.target);

    if (targetNode == null || !isInteractiveTaskNode(targetNode)) {
      return null;
    }

    for (const section of targetNode.data.sections ?? []) {
      for (const row of section.sectionRows) {
        if (doesDataFlowEdgeHandleReferenceRow(edge.targetHandle, row.clientId)) {
          return row;
        }
      }
    }
    return null;
  };

  const getSourceField = (rowIndex: number): TaskSchemaNodeRowData | null => {
    const edge = props.edges[rowIndex];
    const sourceNode = props.flowchart.nodes.find((node) => node.id === edge.source);

    if (sourceNode == null || !isInteractiveTaskNode(sourceNode)) {
      return null;
    }

    for (const section of sourceNode.data.sections ?? []) {
      for (const row of section.sectionRows) {
        if (doesDataFlowEdgeHandleReferenceRow(edge.sourceHandle, row.clientId)) {
          return row;
        }
      }
    }
    return null;
  };

  const {transitionsById, ptdvPairToTransitionIdIndex} = useTransitionsContext();
  const staticNodes = useMemo(() => props.flowchart.nodes.map((node) => node.data), [props.flowchart.nodes]);

  const getTransitionSourceForEdge = (dataFlowEdge: FlowchartStaticEdgeData): TransitionSource =>
    getTransitionSourceForEdgeWithDefault({
      edge: dataFlowEdge,
      nodes: staticNodes,
      transitionsById,
      ptdvPairToTransitionIdIndex,
      nodesForDefault: staticNodes,
    });

  const getCustomMappingDataForEdge = (edge: FlowchartStaticEdgeData): CustomMappingData | null =>
    getCustomMappingForEdge({
      edge,
      nodes: staticNodes,
      transitionsById,
      ptdvPairToTransitionIdIndex,
    });

  const getLookupConfigForEdge = (edge: FlowchartStaticEdgeData): LookupConfigSpec =>
    getCustomMappingDataForEdge(edge)?.lookupConfig ??
    buildDefaultSchemaFieldMapperConfigFromDataFlowEdge({
      clientId: getClientId(),
      dataFlowEdge: edge,
      nodes: props.flowchart.nodes.map((node) => node.data),
      transitions: transitionsById,
    }).lookupConfig;

  const ERROR_TYPES_TO_SHOW_IN_TABLE: Array<FlowchartValidationErrorType> = [
    ProcessFlowchartErrorType.DATA_FLOW_EDGE_MAPPING_FIELD_MISMATCH,
  ];

  const validationErrorsForTable = props.validationErrors.filter((error) =>
    props.edges.some(
      (edge) => edge.id === error.object?.clientId && ERROR_TYPES_TO_SHOW_IN_TABLE.includes(error.errorType)
    )
  );

  return (
    <>
      <ValidationErrorRenderer validationErrors={validationErrorsForTable} />
      <Table
        numRows={props.edges.length}
        getRowClassName={(rowIndex) => {
          const edge = props.edges[rowIndex];
          const hasError = validationErrorsForTable.some((error) => error.object?.clientId === edge.id);
          return hasError ? css(styles.errorRow) : '';
        }}
      >
        <Column
          header={<Cell className={css(styles.wrappingCell)}>Map from</Cell>}
          renderCell={(rowIndex) => {
            const fromSchema = getMappingSourceSchema(
              props.edges[rowIndex],
              props.flowchart.nodes,
              props.allSchemasById,
              getTransitionSourceForEdge(props.edges[rowIndex])
            );
            if (fromSchema == null) {
              return <Cell />;
            }
            const sourceField = getSourceField(rowIndex);
            if (sourceField == null) {
              return <Cell />;
            }
            const edge = props.edges[rowIndex];
            const customMappingData = getCustomMappingDataForEdge(edge);
            return (
              <Cell className={css(styles.wrappingCell)}>
                {
                  // Only show the lookup step chain if the lookup config is custom (not field to field)
                  customMappingData?.lookupConfig == null &&
                  sourceField.sectionType !== TaskSchemaNodeSectionType.MAP_FROM_START ? (
                    <FieldDisplay
                      field={{
                        fieldIcon: sourceField.fieldIcon,
                        fieldName: sourceField.fieldName,
                      }}
                    />
                  ) : (
                    <StaticLookupConfigSummaryChip
                      baseSchema={fromSchema}
                      lookupConfig={getLookupConfigForEdge(edge)}
                      context={{flowchartTaskSchema: props.flowchartTaskSchema}}
                    />
                  )
                }
              </Cell>
            );
          }}
        />
        <Column
          header={<Cell className={css(styles.wrappingCell)}>Map to</Cell>}
          renderCell={(rowIndex) => {
            const targetField = getTargetField(rowIndex);
            if (targetField == null) {
              return <Cell />;
            }

            return (
              <Cell className={css(styles.wrappingCell)}>
                <FieldDisplay
                  field={{
                    fieldIcon: targetField.fieldIcon,
                    fieldName: targetField.fieldName,
                  }}
                />
              </Cell>
            );
          }}
        />
        {didInitialConfigHaveConditionalMapping && (
          <Column
            header={
              <Cell className={css(styles.wrappingCell)}>
                <span>
                  <span>Required</span>
                  <Tooltip text="Check the box to prevent items with empty or null values for the specified field from being created.">
                    <BenchlingIcon icon="info-circle" className={css(styles.helpIcon)} />
                  </Tooltip>
                </span>
              </Cell>
            }
            renderCell={(rowIndex) => {
              const inputId = _.uniqueId('resultMustBeNonNullToMap');
              const edge = props.edges[rowIndex];
              const customMappingData = getCustomMappingDataForEdge(edge);
              const isChecked = customMappingData?.resultMustBeNonNullToMap ?? false;
              return (
                <Cell>
                  <CheckboxInput
                    id={inputId}
                    checked={isChecked}
                    label="Require field to map"
                    labelStyle={LabelStyle.HIDDEN}
                    onChange={(e) => {
                      props.onChangeCustomMappingData(edge.id, {
                        ...customMappingData,
                        resultMustBeNonNullToMap: e.target.checked,
                      });
                    }}
                    {...getConditionalMappingCheckboxProps({
                      isLocked: props.locked,
                      isChecked,
                    })}
                  />
                </Cell>
              );
            }}
          />
        )}
        <Column
          header={<Cell className={css(styles.actionsCell)} />}
          renderCell={(rowIndex) => {
            const targetField = getTargetField(rowIndex);
            const sourceField = getSourceField(rowIndex);
            if (targetField == null || sourceField == null) {
              return null;
            }
            const edge = props.edges[rowIndex];
            const customMappingData = getCustomMappingDataForEdge(edge);
            return (
              <EditMappingLookupCell
                targetField={targetField}
                sourceField={sourceField}
                dataFlowEdge={edge}
                nodes={props.flowchart.nodes}
                allSchemasById={props.allSchemasById}
                flowchartTaskSchema={props.flowchartTaskSchema}
                transitionSource={getTransitionSourceForEdge(edge)}
                lookupConfig={getLookupConfigForEdge(edge)}
                onChangeLookupConfig={(lookupConfig) => {
                  props.onChangeCustomMappingData(edge.id, {
                    ...customMappingData,
                    lookupConfig: lookupConfig ?? undefined,
                  });
                }}
                isCustomLookupConfig={customMappingData?.lookupConfig != null}
                locked={props.locked}
              />
            );
          }}
        />
      </Table>
    </>
  );
}

function TransitionMappingsTableForMultipleConnections(
  props: MappingsTableProps & {
    getEdgeGroupKey: (edge: FlowchartEdge) => string;
    connectedNodeLabel: string;
  }
): JSX.Element {
  const edgesByNodeId = groupBy(props.edges, props.getEdgeGroupKey);
  const sortedNodes = props.flowchart.nodes
    .filter((node) => edgesByNodeId[node.id] != null)
    .sort((leftNode, rightNode) => leftNode.data.nodeName.localeCompare(rightNode.data.nodeName));

  if (props.edges.length === 0) {
    return <NoMappings />;
  }

  return (
    <>
      <p className={css(styles.tableDescriptionText)}>{props.tableDescription}</p>
      {sortedNodes.map((node) => {
        const edges = edgesByNodeId[node.id];
        return (
          <div
            className={css(styles.tableContainer)}
            key={node.id}
            {...getTestProps({
              component: 'TransitionFieldInfoSection',
              element: 'mappingsTable',
            })}
          >
            <div className={css(styles.connectedNodeLabel)}>
              <div>{props.connectedNodeLabel} </div>
              <Chip size={ChipSize.MINI} icon={ModelTypeConstants.PROCESS_TASK} text={node.data.nodeName} />
            </div>
            <TransitionMappingsTable {...props} edges={edges} />
          </div>
        );
      })}
    </>
  );
}

function InboundMappingsTable(props: IntermediateMappingsTableProps): JSX.Element {
  const inboundEdges = props.flowchart.edges.filter((edge) => {
    if (
      edge.type !== ProcessFlowchartEdgeType.DATA_FLOW ||
      edge.target !== props.node.id ||
      edge.targetHandle == null
    ) {
      return false;
    }
    const targetFieldClientInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);
    return targetFieldClientInfo.fieldClientId === getSchemaFieldIdFromRowIdentifier(props.selectedFieldId);
  });

  return (
    <TransitionMappingsTableForMultipleConnections
      {...props}
      edges={inboundEdges}
      getEdgeGroupKey={(edge) => edge.source}
      connectedNodeLabel="From"
    />
  );
}

function OutboundMappingsTable(props: IntermediateMappingsTableProps): JSX.Element {
  const outboundEdges = props.flowchart.edges.filter((edge) => {
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW || edge.sourceHandle == null) {
      return false;
    }
    const sourceFieldClientInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);

    if (
      props.node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS &&
      sourceFieldClientInfo.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
    ) {
      return sourceFieldClientInfo.fieldClientId === getSchemaFieldIdFromRowIdentifier(props.selectedFieldId);
    } else if (edge.source !== props.node.id) {
      return false;
    }

    return sourceFieldClientInfo.fieldClientId === getSchemaFieldIdFromRowIdentifier(props.selectedFieldId);
  });

  return (
    <TransitionMappingsTableForMultipleConnections
      {...props}
      edges={outboundEdges}
      getEdgeGroupKey={(edge) => edge.target}
      connectedNodeLabel="To"
    />
  );
}

export default function TransitionFieldInfoSection(props: TransitionFieldInfoSectionProps): JSX.Element {
  const selectedField = useMemo(() => {
    assert(isInteractiveTaskNode(props.node), 'Expected the selected node to be an interactive task node', {
      node: props.node,
    });

    for (const section of props.node.data.sections) {
      for (const row of section.sectionRows) {
        if (row.clientId === props.selectedFieldId) {
          return row;
        }
      }
    }

    assert(
      false,
      `Expected to find the selected field ${props.selectedFieldId} in the selected node ${props.node.data.nodeName}`,
      {
        selectedFieldId: props.selectedFieldId,
        nodeId: props.node,
      }
    );
  }, [props.selectedFieldId, props.node]);

  // The selected node is the source if there is a control flow edge from the selected node to another node.
  // Otherwise the node is a "target" node.
  const nodeIsSource = props.flowchart.edges.some(
    (edge) =>
      edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW &&
      edge.source === props.node.id &&
      edge.sourceHandle != null
  );
  const nodeIsTarget = props.flowchart.edges.some(
    (edge) =>
      edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW &&
      edge.target === props.node.id &&
      edge.targetHandle != null
  );

  return (
    <>
      <Row bufferRowReset={true}>
        <Col sm={4}>
          <SectionWrapper headerName="Name" elementName="name">
            <div className={css(styles.wrappingText)}>{selectedField.fieldName}</div>
          </SectionWrapper>
        </Col>
        <Col sm={4}>
          <SectionWrapper headerName="Definition" elementName="definition">
            <FieldDisplay
              field={{
                fieldIcon: selectedField.fieldIcon,
                fieldName: selectedField.fieldTypeLabel,
              }}
            />
          </SectionWrapper>
        </Col>
        <Col sm={4}>
          <SectionWrapper headerName="Task" elementName="task">
            <Button
              buttonStyle={ButtonStyle.LINK}
              onClick={() => {
                props.onChangeSelectedFieldId(null);
              }}
              text={props.node.data.nodeName}
            />
          </SectionWrapper>
        </Col>
      </Row>

      <Row bufferRowReset={true}>
        <div className={css(styles.mappingTableContainer)}>
          {nodeIsTarget && (
            <SectionWrapper headerName="Inbound mappings" elementName="inbound-mappings">
              <InboundMappingsTable
                tableDescription="Configure how data is mapped to this field."
                {...props}
              />
            </SectionWrapper>
          )}
          {nodeIsSource && (
            <SectionWrapper headerName="Outbound mappings" elementName="outbound-mappings">
              <OutboundMappingsTable
                tableDescription="Configure how data is mapped from this field."
                {...props}
              />
            </SectionWrapper>
          )}
        </div>
      </Row>
    </>
  );
}

const styles = StyleSheet.create({
  tableDescriptionText: {
    marginBottom: 16,
  },
  mappingTableContainer: {
    padding: '0 15px',
  },
  fieldDisplay: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 4,
  },
  helpIcon: {
    marginLeft: 8,
  },
  editMappingButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 4,
  },
  actionIcon: {
    color: GRAY_DARK,
    ':hover': {
      color: BLUE_LIGHT,
    },
  },
  noMappings: {
    paddingTop: 16,
    paddingBottom: 16,
  },
  noMappingsText: {
    color: Color.GRAY_600,
  },
  tableContainer: {
    marginTop: 16,
  },
  connectedNodeLabel: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    gap: 4,
    marginBottom: 16,
    alignItems: 'center',
  },
  wrappingCell: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    width: 'auto',
    textWrap: 'pretty',
  },
  actionsCell: {
    width: '50px',
  },
  errorRow: {
    backgroundColor: RED_POWDER,
    borderLeft: `4px solid ${RED}`,
  },
  wrappingText: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
});
